/**
 * Component: Widget version handshake
 * SpecReferenceID: WIDGET-V1
 * Requirements: WIDGET-V1-R18
 *
 * First call the widget makes during initialisation:
 * fetch GET /api/widget/version, compare the returned
 * backend semver against the widget's declared compatible
 * backend range, and either proceed or raise a
 * ``WidgetVersionMismatchError`` that the init pipeline
 * converts into the documented error state.
 *
 * Compliance role: this is the widget's ISO 14971 control
 * for version-mismatch risk (widget.md §4.5). A clinician
 * never sees widget output when the widget/backend
 * combination has not been validated as compatible.
 *
 * Why a local semver comparator rather than pulling in the
 * ``semver`` npm package:
 *
 *   - Adds ~30 KB to the UMD bundle for functionality we
 *     use in exactly one place.
 *   - The widget's compatible range is a fixed shape at
 *     build time (caret or exact pin on a major version);
 *     the full semver spec is overkill.
 *   - Every dependency we add to the widget is a SOUP
 *     entry that has to be registered — see CE-compliance
 *     requirements.
 *
 * The lightweight comparator in ``isSemverInRange`` below
 * supports the two range forms the widget actually needs:
 * exact (``"1.2.3"``) and caret (``"^1.2.3"``, i.e.
 * ``>=1.2.3 <2.0.0``). Adding a third form (``~``, ``>=``)
 * should come with a unit test covering the new shape.
 */

import type { WidgetApiClient } from '../api/client';
import { WidgetError } from '../api/errors';

/**
 * Raised when the backend API version is outside the
 * widget's declared compatible range. Carries both
 * versions so the host error callback can render them in
 * the documented error message without parsing the error
 * string.
 */
export class WidgetVersionMismatchError extends WidgetError {
  readonly widgetVersion: string;
  readonly backendVersion: string;
  readonly compatibleRange: string;

  constructor(
    widgetVersion: string,
    backendVersion: string,
    compatibleRange: string,
  ) {
    super(
      `Widget version ${widgetVersion} is not compatible with Galen backend ` +
        `version ${backendVersion} (widget requires backend "${compatibleRange}"). ` +
        'Please contact your administrator to upgrade the widget or the deployment.',
    );
    this.name = 'WidgetVersionMismatchError';
    this.widgetVersion = widgetVersion;
    this.backendVersion = backendVersion;
    this.compatibleRange = compatibleRange;
  }
}

/**
 * Response shape returned by ``GET /api/widget/version``.
 * Mirrors the ``WidgetVersionResponse`` Pydantic schema
 * shipped in API-GATEWAY-V1-R15.
 */
export interface WidgetBackendVersion {
  api_version: string;
}

/**
 * Parse a semver string into ``[major, minor, patch]``.
 * Extra pre-release / build-metadata suffixes (``-rc.1``,
 * ``+build.5``) are truncated for the purpose of range
 * comparison — the widget only cares about the major.minor
 * .patch triplet.
 *
 * Throws on malformed input so the caller can surface a
 * clear error rather than silently treating garbage as
 * 0.0.0.
 */
export function parseSemver(version: string): [number, number, number] {
  const match = /^(\d+)\.(\d+)\.(\d+)(?:[-+].*)?$/.exec(version.trim());
  if (!match) {
    throw new Error(`Not a valid semver string: "${version}"`);
  }
  return [
    Number.parseInt(match[1]!, 10),
    Number.parseInt(match[2]!, 10),
    Number.parseInt(match[3]!, 10),
  ];
}

/**
 * Compare two parsed semvers. Returns -1, 0, or 1 in the
 * familiar Array.prototype.sort comparator convention.
 */
function compareSemver(
  a: readonly [number, number, number],
  b: readonly [number, number, number],
): -1 | 0 | 1 {
  for (let i = 0; i < 3; i++) {
    if (a[i]! < b[i]!) return -1;
    if (a[i]! > b[i]!) return 1;
  }
  return 0;
}

/**
 * Test whether ``candidate`` (e.g. the backend's reported
 * api_version) satisfies ``range`` (the widget's declared
 * compatible backend range).
 *
 * Supported range forms:
 *
 *   - Exact:  ``"1.2.3"``    -> matches only 1.2.3
 *   - Caret:  ``"^1.2.3"``   -> matches >=1.2.3 <2.0.0
 *
 * Any other prefix is treated as an exact match (so
 * ``">=1.0.0"`` currently only matches 1.0.0 verbatim — if
 * we add new range forms later, extend this function and
 * add unit tests).
 */
export function isSemverInRange(candidate: string, range: string): boolean {
  const trimmed = range.trim();
  const candidateParts = parseSemver(candidate);

  if (trimmed.startsWith('^')) {
    const baseParts = parseSemver(trimmed.slice(1));
    const [baseMajor] = baseParts;
    const [candidateMajor] = candidateParts;
    // Caret requires same major AND candidate >= base.
    if (candidateMajor !== baseMajor) return false;
    return compareSemver(candidateParts, baseParts) >= 0;
  }

  // Exact match fallback.
  const targetParts = parseSemver(trimmed);
  return compareSemver(candidateParts, targetParts) === 0;
}

/**
 * Execute the R18 handshake: fetch the backend's
 * ``api_version``, compare against the widget's declared
 * ``compatibleBackendRange``, and either resolve with the
 * backend version (for logging) or throw a
 * ``WidgetVersionMismatchError``.
 *
 * @param client Configured ``WidgetApiClient`` (the init
 *   pipeline creates it before the handshake).
 * @param widgetVersion Widget SDK semver — supplied by
 *   the caller (typically ``GALEN_WIDGET_VERSION``) so this
 *   module has no direct dependency on ``src/index.ts``.
 * @param compatibleBackendRange Range the widget was built
 *   to support (e.g. ``"^1.1.0"``).
 */
export async function performVersionHandshake(
  client: WidgetApiClient,
  widgetVersion: string,
  compatibleBackendRange: string,
): Promise<WidgetBackendVersion> {
  const response = await client.getJson<WidgetBackendVersion>(
    '/api/widget/version',
  );
  if (!response || typeof response.api_version !== 'string') {
    throw new WidgetError(
      `GET /api/widget/version returned an unexpected shape: ${JSON.stringify(response)}`,
    );
  }

  if (!isSemverInRange(response.api_version, compatibleBackendRange)) {
    throw new WidgetVersionMismatchError(
      widgetVersion,
      response.api_version,
      compatibleBackendRange,
    );
  }

  return response;
}
